import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { postItem } from '../../support/queries';
import {
  useFetchAllPartner,
  useFetchAllServiceCreation,
  useFetchAllGeo,
  useFetchAllAccountSegment,
  useFetchAllAccountType,
  useFetchAllMarketSegment,
} from '../../hooks/mssp-api-hooks';
import {
  HbrDivider,
  HbrInput,
  HbrTag,
} from '@harbor/elements-react-standalone';
import StbButton from '../security-toolbox-ui/StbButton';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import { CacheKey } from '../../types/cache-keys';
import { renderErrorMessage } from '../security-toolbox-ui/security-toolbox-ui-utils';


const MspServiceCreation: React.FC = () => {
  const queryClient = useQueryClient();

  const [openAddPartnerModal, setOpenAddPartnerModal] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState(''); // Track the input value for the partner name
  const [managedResale, setManagedResale] = useState('Managed'); // Track the value of the Managed / Resale dropdown
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to refresh data
  const navigate = useNavigate();

  // Fetch all partners
  const { data: partners, isLoading } = useFetchAllPartner();

  // Fetch all service creations
  const { data: fetchedServiceCreations } = useFetchAllServiceCreation();

  // Fetch additional data
  const { data: geo } = useFetchAllGeo();
  const { data: accountSegments } = useFetchAllAccountSegment();
  const { data: accountTypes } = useFetchAllAccountType();
  const { data: marketSegments } = useFetchAllMarketSegment();

  const [partnerNameTouched, setPartnerNameTouched] = useState(false);
  const [partnerCreated, setPartnerCreated] = useState<string | undefined>(undefined);

  const refreshData = () => {
    setRefreshTrigger((prev) => prev + 1); // Trigger a refresh
  };

  const handleAddPartnerClick = () => {
    setNewPartnerName(''); // Reset the input value when opening the modal
    setManagedResale('Managed'); // Reset the dropdown to default value
    setOpenAddPartnerModal(true);
  };

  const handleSaveNewPartner = async () => {
    const response = await postItem('db/partner', { name: newPartnerName, managed_resale: managedResale }); // Post the new partner
    if (response && response.id) {
      setPartnerCreated(response.id);
      queryClient.invalidateQueries({ queryKey: [CacheKey.AllPartner] });
    }
  };

  useEffect(() => {
    if (!openAddPartnerModal && partnerCreated) {
      navigate(`/mssp/${partnerCreated}`); // Navigate to the new partner's page
    }
  }, [openAddPartnerModal, partnerCreated, navigate]);

  return (
    <div className="app-mssp-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '.5rem' }}>
        <div className="atm-typography-h4 atm-text-content-primary-default">
          MSP Service Creations
        </div>
        <StbButton role="add" onClick={handleAddPartnerClick} className="float-right" >
          Add Partner
        </StbButton>
      </div>
      <br />
      {isLoading ? (
        <Typography>Loading partners...</Typography>
      ) : (
        <div className="hbr-css__grid-md"> {/* Responsive 3-column grid */}
          {partners?.map((partner) => (
            <div
              className="hbr-css__container-card"
              data-col-span="sm"
              data-tablet-col-span="xl"
              key={partner.id}
              style={{ height: '100%' }} // Ensure consistent card height
            >
              <div className="hbr-css__layout-col-2xs">
                <div slot="header" className="atm-card-header-demo">
                  {partner.name}
                </div>
                <HbrTag size="small" style={{ paddingLeft: 'var(--hbr-spacing-xs)' }}>
                  {accountTypes?.find((type) => type.id === partner.account_type)?.name || 'N/A'}
                </HbrTag>
              </div>
              <HbrDivider />
              <dl className="atm-key-value-pairs">
                <dt>Account Segment</dt>
                <dd>
                  {accountSegments?.find((segment) => segment.id === partner.account_segment)?.name || 'N/A'}
                </dd>
                <dt>Geo</dt>
                <dd>
                  {geo?.find((g) => g.id === partner.geo)?.name || 'N/A'}
                </dd>
                <dt>Market Segments</dt>
                <dd>
                  {partner.market_segment
                    ?.map((id) => marketSegments?.find((segment) => segment.id === id)?.name)
                    .join(', ') || 'N/A'}
                </dd>
              </dl>
              <div slot="footer" className="atm-card-footer-demo">
                <StbButton onClick={() => navigate(`/mssp/${partner.id}`)}>
                  View Details
                </StbButton>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Partner Modal */}
      <StbApiModal
        open={openAddPartnerModal}
        title="Add New Partner"
        role="save"
        submitLabel="Add"
        onClose={async () => setOpenAddPartnerModal(false)}
        onSubmit={handleSaveNewPartner}
        submitDisabled={!newPartnerName.trim()}
      >
        <>
          {/* Input for Partner Name */}
          <HbrInput
            value={newPartnerName}
            onHbr-input={(e) => {
              setNewPartnerName(e.target.value)
              setPartnerNameTouched(true);
            }}
            error={partnerNameTouched && !(newPartnerName ?? '').trim().length}
          >
            <div slot="label">Partner Name <em className="text-gray-500">(required)</em></div>
            {renderErrorMessage(partnerNameTouched && !(newPartnerName ?? '').trim().length, 'Please enter a partner name')}
          </HbrInput>

          {/* Dropdown for Managed / Resale */}
          <FormControl fullWidth style={{ marginTop: '20px' }}>
            <InputLabel id="managed-resale-label">Managed / Resale</InputLabel>
            <Select
              labelId="managed-resale-label"
              value={managedResale}
              onChange={(e) => setManagedResale(e.target.value)} // Track dropdown value
              label="Managed / Resale"
            >
              <MenuItem value="Managed">Managed</MenuItem>
              <MenuItem value="Resale">Resale</MenuItem>
              <MenuItem value="Both">Both</MenuItem>
              <MenuItem value="Managed (non-procured)">Managed (non-procured)</MenuItem>
            </Select>
          </FormControl>
        </>
      </StbApiModal>
    </div>
  );
};

export default MspServiceCreation;